// Game Play Page
// Page for playing Heart Games with unified question deck

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHeartGames } from '../../hooks';
import { useAuthContext } from '../../providers';
import { GameAnswer, HeartScore } from '../../types/heartGames';
import { QuestionCardDeck } from '../../components/HeartGames/QuestionCardDeck/QuestionCardDeck';
import { QuestionService } from '../../services/HeartGames/questionService';
import { GameService } from '../../services/HeartGames/gameService';
import { ScoringService } from '../../services/HeartGames/scoringService';
import styled from 'styled-components';
import {
  HeartGamesContainer,
  HeartGamesHeader,
  HeartGamesTitle,
  HeartGamesButton,
  HeartGamesLoading,
  HeartGamesError,
} from '../../styles/HeartGames/Shared.styles';

const HeaderSpacer = styled.div`
  width: 80px;

  @media (max-width: 768px) {
    width: 48px;
  }

  @media (max-width: 320px) {
    width: 40px;
  }
`;

export const GamePlay: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { games, isLoading, error } = useHeartGames(user?.id);

  const selectedGame = useMemo(() =>
    games.find(g => g.id === gameId) || null,
    [games, gameId]);

  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<GameAnswer[]>([]);

  // Session / resume state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [initialSelectedAnswers, setInitialSelectedAnswers] = useState<Record<string, string>>({});
  const [sessionReady, setSessionReady] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const startTimeRef = useRef<number>(Date.now());

  const [finalAnswers, setFinalAnswers] = useState<GameAnswer[]>([]);
  const [finalScore, setFinalScore] = useState<HeartScore | null>(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);

  useEffect(() => {
    if (!selectedGame) return;
    let cancelled = false;
    (async () => {
      // Loaded from the DB (heart_game_questions); falls back to the bundled
      // set if the backend is unreachable.
      const gameQuestions = await QuestionService.fetchQuestionsByCategory(
        selectedGame.category,
        20 // Force 20 questions as per request
      );
      if (!cancelled) setQuestions(gameQuestions);
    })();
    return () => { cancelled = true; };
  }, [selectedGame]);

  // Persist every answer as it's made (the backend overwrites the session's
  // answer blob each call), so progress is saved even if the user leaves.
  const handleAnswer = useCallback((answer: GameAnswer) => {
    setAnswers((prev) => {
      const next = [...prev, answer];
      if (sessionId) {
        GameService.submitAnswers(sessionId, next).catch((err) =>
          console.error('Failed to save answer:', err));
      }
      return next;
    });
  }, [sessionId]);

  // On finishing: compute the real MBTI score from the answers and mark the
  // session complete (writes game_results + user_game_progress for matching).
  const handleComplete = useCallback((allAnswers: GameAnswer[]) => {
    setFinalAnswers(allAnswers);
    setShowBadgeModal(true);

    const score = ScoringService.computeScoreFromQuestions(allAnswers, questions);
    setFinalScore(score);

    if (sessionId) {
      const insights = ScoringService.generateInsights(score);
      const recommendations = ScoringService.generateRecommendations(score);
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      GameService.completeGameSession(sessionId, {
        gameId: gameId ?? '',
        userId: user?.id ?? '',
        score,
        answers: allAnswers,
        duration,
        completedAt: new Date(),
        insights,
        recommendations,
      }).catch((err) => console.error('Failed to complete session:', err));
    }
  }, [sessionId, questions, gameId, user?.id]);

  const handleProceedToResults = () => {
    // Pass the freshly computed score so results render instantly, even before
    // the completion write has round-tripped (the page also fetches as a fallback).
    navigate(`/heart-games/${gameId}/results`, {
      state: { score: finalScore, answers: finalAnswers }
    });
  };

  const [showExitModal, setShowExitModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [hasShownMilestone, setHasShownMilestone] = useState(false);

  useEffect(() => {
    if (answers.length === 10 && !hasShownMilestone) {
      setShowMilestoneModal(true);
      setHasShownMilestone(true);
    }
  }, [answers.length, hasShownMilestone]);

  // Resolve the game session once questions are loaded: resume the user's
  // latest session (restoring answered questions) or start a fresh one.
  // Account-based, so progress follows the user across devices/browsers.
  useEffect(() => {
    if (!selectedGame || selectedGame.type === 'challenge' || questions.length === 0 || !user?.id || !gameId) {
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const latest = await GameService.getLatestSession(gameId);
        if (cancelled) return;

        if (latest) {
          const restoredMap: Record<string, string> = {};
          const restoredAnswers: GameAnswer[] = [];
          for (const a of ((latest.answers as any[]) ?? [])) {
            if (a && a.questionId && a.optionId) {
              restoredMap[a.questionId] = a.optionId;
              restoredAnswers.push({
                questionId: a.questionId,
                optionId: a.optionId,
                responseTime: a.responseTime ?? 0,
                timestamp: a.timestamp ? new Date(a.timestamp) : new Date(),
              });
            }
          }
          setSessionId(latest.id);
          setInitialSelectedAnswers(restoredMap);
          setAnswers(restoredAnswers);
          setHasShownMilestone(restoredAnswers.length >= 10);

          const allAnswered = questions.every((q) => restoredMap[q.id]);
          if (latest.isCompleted || allAnswered) {
            setAlreadyCompleted(true);
          }
        } else {
          const session = await GameService.startGameSession(gameId, user.id);
          if (cancelled) return;
          setSessionId(session.id);
        }
      } catch (err) {
        console.error('Failed to resolve game session:', err);
      } finally {
        if (!cancelled) {
          startTimeRef.current = Date.now();
          setSessionReady(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [selectedGame, questions, user?.id, gameId]);

  const handleExitRequest = () => {
    setShowExitModal(true);
  };

  const handleConfirmExit = () => {
    // Add save logic here if needed in the future
    navigate('/heart-games');
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  const gameContent = useMemo(() => {
    if (!selectedGame) return null;

    if (selectedGame.type === 'challenge') {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <div style={{
            background: '#ff8c42',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            color: 'white',
            fontSize: '2.5rem'
          }}>
            📸
          </div>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>Daily Moments Challenge</h2>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
            {selectedGame.description}
          </p>

          <div style={{ background: 'var(--bg-secondary)', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', textAlign: 'left' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>How to Participate:</h3>
            <ol style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              <li>Go to your <strong>Profile</strong> using the bottom navigation.</li>
              <li>Tap the <strong>Profile Icon</strong> with the <span style={{ color: '#ff8c42', fontWeight: 'bold' }}>+</span> badge.</li>
              <li>Capture or upload a Moment from your day.</li>
              <li>Share it to complete the challenge!</li>
            </ol>
          </div>

          <HeartGamesButton
            variant="primary"
            onClick={() => navigate('/profile/view')}
            style={{
              width: '100%',
              justifyContent: 'center',
              padding: '1rem',
              background: 'linear-gradient(135deg, #ff8c42 0%, #ffb366 100%)',
              border: 'none'
            }}
          >
            Go to Profile
          </HeartGamesButton>
        </div>
      );
    }

    if (questions.length === 0) {
      return <HeartGamesError>No questions available for this category.</HeartGamesError>;
    }

    if (!sessionReady) {
      return <HeartGamesLoading>Loading your progress…</HeartGamesLoading>;
    }

    if (alreadyCompleted) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', maxWidth: '520px', margin: '0 auto' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h2 style={{ fontSize: '1.4rem', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
            You've completed this game
          </h2>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '1.75rem' }}>
            Your answers are saved and will be used to match you with compatible people —
            no need to answer these questions again.
          </p>
          <HeartGamesButton
            variant="primary"
            onClick={() => navigate(`/heart-games/${gameId}/results`)}
            style={{ width: '100%', justifyContent: 'center', padding: '1rem' }}
          >
            View Your Results
          </HeartGamesButton>
        </div>
      );
    }

    return (
      <QuestionCardDeck
        questions={questions}
        onAnswer={handleAnswer}
        onComplete={handleComplete}
        initialSelectedAnswers={initialSelectedAnswers}
      />
    );
  }, [selectedGame, questions, sessionReady, alreadyCompleted, initialSelectedAnswers, gameId, navigate, handleAnswer, handleComplete]);

  if (isLoading) {
    return (
      <HeartGamesContainer>
        <HeartGamesLoading>Loading</HeartGamesLoading>
      </HeartGamesContainer>
    );
  }

  if (error || !selectedGame) {
    return (
      <HeartGamesContainer>
        <HeartGamesError>
          {error || 'Game not found'}
        </HeartGamesError>
      </HeartGamesContainer>
    );
  }

  return (
    <HeartGamesContainer>
      <HeartGamesHeader>
        <HeartGamesButton variant="outline" onClick={handleExitRequest}>
          ← Back
        </HeartGamesButton>
        <HeartGamesTitle>
          {selectedGame.icon} {selectedGame.name}
        </HeartGamesTitle>
        <HeaderSpacer />
      </HeartGamesHeader>

      {gameContent}

      {showExitModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'var(--bg-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'var(--bg-card)', padding: '2rem', borderRadius: '16px', maxWidth: '90%', width: '400px',
            boxShadow: 'var(--shadow-medium)'
          }}>
            <h3 style={{ marginTop: 0, fontSize: '1.25rem', color: 'var(--text-primary)' }}>Exit Game?</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>Are you sure you want to exit? Your progress matches will be saved.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <HeartGamesButton variant="outline" onClick={handleCancelExit}>Cancel</HeartGamesButton>
              <HeartGamesButton variant="primary" onClick={handleConfirmExit}>Exit Game</HeartGamesButton>
            </div>
          </div>
        </div>
      )}

      {showMilestoneModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'var(--bg-card)', padding: '2.5rem', borderRadius: '24px', maxWidth: '90%', width: '380px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            textAlign: 'center',
            border: '2px solid #ff8c42',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: '-50px', left: '0', right: '0', height: '150px',
              background: 'linear-gradient(180deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 140, 66, 0) 100%)',
              zIndex: 0
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🏆</div>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', color: 'var(--text-primary)', fontWeight: '800' }}>
                You're on Fire!
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', lineHeight: '1.5', marginBottom: '2rem' }}>
                You've answered <strong>10 questions</strong>! Keep going to earn the exclusive <span style={{ color: '#ff8c42', fontWeight: 'bold' }}>Heart Choices Badge</span>.
              </p>

              <HeartGamesButton
                variant="primary"
                onClick={() => setShowMilestoneModal(false)}
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  padding: '1rem',
                  boxShadow: '0 4px 15px rgba(255, 140, 66, 0.3)'
                }}
              >
                Keep Playing
              </HeartGamesButton>
            </div>
          </div>
        </div>
      )}

      {showBadgeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: 'var(--bg-card)', padding: '2.5rem', borderRadius: '24px', maxWidth: '90%', width: '380px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
            textAlign: 'center',
            border: '2px solid #ff8c42',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: '-60px', left: '-50%', right: '-50%', height: '200px',
              background: 'radial-gradient(circle, rgba(255, 140, 66, 0.2) 0%, rgba(255, 140, 66, 0) 70%)',
              zIndex: 0
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: '4rem', marginBottom: '1.5rem', filter: 'drop-shadow(0 0 10px rgba(255, 140, 66, 0.5))' }}>🏅</div>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.6rem', color: 'var(--text-primary)', fontWeight: '800' }}>
                Badge Unlocked!
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', lineHeight: '1.5', marginBottom: '2rem' }}>
                Congratulations! You've answered all 20 questions and earned the <span style={{ color: '#ff8c42', fontWeight: 'bold' }}>Heart Choices Badge</span>.
                <br /><br />
                It will now be displayed on your profile.
              </p>

              <HeartGamesButton
                variant="primary"
                onClick={handleProceedToResults}
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  padding: '1rem',
                  boxShadow: '0 4px 15px rgba(255, 140, 66, 0.3)',
                  background: 'linear-gradient(135deg, #ff8c42 0%, #ff6b35 100%)'
                }}
              >
                View Badge & Results
              </HeartGamesButton>
            </div>
          </div>
        </div>
      )}
    </HeartGamesContainer>
  );
};
